import StreamPlayer from "@/components/StreamPlayer";
import AuthContext from "@/context/auth";
import apiClient from "@/libs/api";
import getPublicUrl from "@/libs/get-public-url";
import { callStore } from "@/libs/stores/call.store";
import { fullscreenStore } from "@/libs/stores/fullscreen.store";
import { loadTXLivePusher } from "@/libs/utils/loadTXLivePusher";
import {
  Avatar,
  Box,
  Button,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Spinner,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  FiMic,
  FiMicOff,
  FiPhoneOff,
  FiRefreshCw,
  FiVideo,
  FiVideoOff
} from "react-icons/fi";


/** Prefer front/user-facing camera; return deviceId or devices[0].deviceId. */
function pickFrontCameraId(devices) {
  if (!devices?.length) return null;
  const lower = (s) => (s || "").toLowerCase();
  const front =
    devices.find(
      (d) =>
        d.facingMode === "user" ||
        /front|user|face|前置|前鏡頭|自拍/.test(lower(d.label || d.deviceName || ""))
    ) || devices[0];
  return front.deviceId;
}

/** True if viewport is portrait (height > width), e.g. typical mobile; false = landscape. */
function isPortraitViewport() {
  if (typeof window === "undefined") return true;
  return window.innerHeight > window.innerWidth;
}

/**
 * Call Modal - handles video/audio call with a creator
 * Reuses StreamPlayer for pulling creator's stream
 * Uses per-minute billing similar to StreamDetail
 */
const CallModal = observer(({ isOpen, onClose, creator, callType = "video" }) => {
  const toast = useToast();
  const { user } = useContext(AuthContext);
  const playerRef = useRef(null);
  
  // Local state
  const [isConnecting, setIsConnecting] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [cameraList, setCameraList] = useState([]);
  const [currentCameraId, setCurrentCameraId] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  /** Player trace: 'idle' | 'loading' | 'playing' | 'error' */
  const [playerStatus, setPlayerStatus] = useState("idle");
  /** Viewport orientation for local preview aspect ratio (updates on resize/orientationchange). */
  const [isPortrait, setIsPortrait] = useState(() => isPortraitViewport());

  // Refs for timers and push
  const durationTimerRef = useRef(null);
  const pollUrlsIntervalRef = useRef(null);
  const pusherRef = useRef(null);
  const localPreviewRef = useRef(null);
  const hasInitiatedRef = useRef(false);
  // Track modal open state for async operations to prevent race conditions
  const modalOpenRef = useRef(isOpen);
  // Retry logic for player
  const retryTimerRef = useRef(null);
  const pushRetryCountRef = useRef(0);
  const MAX_PUSH_RETRIES = 50;
  // Audio fallback for voice calls with audio-only streams
  const audioFallbackRef = useRef(null);

  useEffect(() => {
    modalOpenRef.current = isOpen;
  }, [isOpen]);

  // Keep viewport orientation in state so local preview aspect ratio updates on rotate
  useEffect(() => {
    const update = () => setIsPortrait(isPortraitViewport());
    update();
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);

  // Hide nav only when modal is actually open (so explore scroll can show nav when call is closed)
  useEffect(() => {
    if (!isOpen) return;
    fullscreenStore.requestHideNav();
    return () => fullscreenStore.releaseHideNav();
  }, [isOpen]);

  // Price per minute
  const pricePerMinute = callType === "video" 
    ? creator?.videoPricePerMinute || 1000 
    : creator?.voicePricePerMinute || 500;

  // Payment is handled automatically by request-auth on the server side
  // when Tencent calls the auth endpoint. No explicit client-side payment needed.

  /**
   * Start duration timer
   */
  const startDurationTimer = useCallback(() => {
    durationTimerRef.current = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);
  }, []);

  /**
   * Start user push stream (TXLivePusher) so creator can see/hear the caller.
   * Voice: mic only. Video: camera + mic. Waits for first frame(s) before startPush per Tencent doc.
   */
  const startUserPush = useCallback(
    async (webrtcUrl, effectiveCallType) => {
      if (!modalOpenRef.current) return; // Don't start if modal is closed
      if (!webrtcUrl || pusherRef.current) return;
      
      const isVideoCall = effectiveCallType === "video";
      try {
        // Verify DOM element exists before starting
        const previewEl = document.getElementById("call-local-preview");
        if (!previewEl) {
          if (modalOpenRef.current && pushRetryCountRef.current < MAX_PUSH_RETRIES) {
            pushRetryCountRef.current += 1;
            console.warn("[CallModal] call-local-preview element not found, retrying...");
            setTimeout(() => startUserPush(webrtcUrl, effectiveCallType), 100);
          }
          return;
        }
        pushRetryCountRef.current = 0;
        
        const loaded = await loadTXLivePusher();
        
        // Check again after async load
        if (!modalOpenRef.current) return;
        
        if (!loaded || !window.TXLivePusher) {
          console.warn("[CallModal] TXLivePusher not available");
          return;
        }
        
        // Double check we don't already have a pusher (race condition)
        if (pusherRef.current) return;

        const livePusher = new window.TXLivePusher();
        livePusher.setAudioQuality("standard");
        livePusher.setRenderView("call-local-preview");
        // Video quality: use orientation so mobile (portrait) and desktop (landscape) get correct aspect ratio.
        // @see https://www.tencentcloud.com/document/product/267/58159 (setVideoQuality + setProperty setVideoResolution/setVideoBitrate)
        const portrait = isPortraitViewport();
        if (isVideoCall) {
          // livePusher.setVideoQuality("720p");
          livePusher.setProperty("setVideoFPS", 25);
          // Override with explicit resolution/bitrate for portrait vs landscape
          if (portrait) {
            livePusher.setProperty("setVideoResolution", { width: 720, height: 1280 });
            livePusher.setProperty("setVideoBitrate", 1500);
          } else {
            livePusher.setProperty("setVideoResolution", { width: 1280, height: 720 });
            livePusher.setProperty("setVideoBitrate", 1500);
          }
        } else {
          livePusher.setVideoQuality("120p");
          livePusher.setProperty("setVideoFPS", 5);
          // livePusher.setProperty("setVideoResolution", { width: 360, height: 640 });
          livePusher.setProperty("setVideoBitrate", 500);
        }
        livePusher.setProperty("enableAudioAEC", true);
        livePusher.setProperty("enableAudioANS", true);
        livePusher.setProperty("enableLog", true);

        // Store pusher immediately so we can clean it up
        pusherRef.current = livePusher;
        
        // If modal closed during setup, clean up immediately
        if (!modalOpenRef.current) {
            try {
              // livePusher.stopCamera();
              // livePusher.stopMicrophone();
              livePusher.stopPush();
              livePusher.destroy && livePusher.destroy();
            } catch(e) {
              console.error("[CallModal] Failed to stop pusher:", e);
            }
            pusherRef.current = null;
            return;
        }

        let hasVideo = false;
        let hasAudio = false;
        let pushStarted = false;

        const maybeStartPush = () => {
          // Check if modal still open
          if (!modalOpenRef.current) return;

          if (pushStarted) return;
          if (isVideoCall && (!hasVideo || !hasAudio)) return;
          if (!isVideoCall && !hasAudio) return;
          
          pushStarted = true;
          livePusher.startPush(webrtcUrl);
        };
        
        // 腾讯云直播推流事件监听
        livePusher.setObserver({
          // 首帧音频采集完成回调
          onCaptureFirstAudioFrame: function () {
            hasAudio = true;
            maybeStartPush();
          },
          // 首帧视频采集完成回调
          onCaptureFirstVideoFrame: function () {
            hasVideo = true;
            maybeStartPush();
          },
          // 推流器错误通知回调
          onError: function (code, msg, extraInfo) {
            console.error("[CallModal Push] Pusher error:", code, msg, extraInfo);
          },
          // 推流器警告回调
          onWarning: function (code, msg, extraInfo) {
            console.warn("[CallModal Push] Pusher warning:", code, msg, extraInfo);
          },
          // 推流连接状态回调
          onPushStatusUpdate: function () {},
          // 推流统计数据回调
          onStatisticsUpdate: function () {}
        });
        
        if (isVideoCall) {
          livePusher.startCamera();
          try {
            const deviceManager = livePusher.getDeviceManager();
            const allDevices = await deviceManager.getDevicesList();
            const devices = allDevices.filter(d => d.type === 'video');
            setCameraList(devices);
            const preferredId = pickFrontCameraId(devices);
            if (preferredId) {
              setCurrentCameraId(preferredId);
              const firstId = devices[0]?.deviceId;
              if (firstId && preferredId !== firstId) {
                await deviceManager.switchCamera(preferredId);
              }
            } else if (devices.length > 0) {
              setCurrentCameraId(devices[0].deviceId);
            }
          } catch (e) {
            console.warn("[CallModal] Failed to get camera list:", e);
          }
        } else {
          setIsVideoOff(true);
        }
        livePusher.startMicrophone();
      } catch (error) {
        console.error("[CallModal] Failed to start user push:", error);
        // Attempt cleanup on error
        if (pusherRef.current) {
            try {
                // pusherRef.current.stopCamera();
                // pusherRef.current.stopMicrophone();
            } catch(e) {}
            pusherRef.current = null;
        }
        
        if (modalOpenRef.current) {
            toast({
              title: "無法開啟麥克風或鏡頭",
              description: "請檢查瀏覽器權限後重試",
              status: "warning",
              duration: 4000,
            });
        }
      }
    },
    [toast]
  );

  /**
   * Initiate the call
   */
  const initiateCall = async () => {
    if (!creator?.creatorId) return;

    setIsConnecting(true);
    callStore.setConnecting(true);

    try {
      // Call API to initiate
      const response = await apiClient.initiateCall(creator.creatorId, callType);
      
      if (!response.success || !response.data) {
        throw new Error(response.error || "Failed to initiate call");
      }

      const callData = response.data;
      callStore.setCall({
        callId: callData.callId,
        callerId: "", // Not used; no call doc
        creatorId: callData.creatorId ?? creator.creatorId,
        callType: callData.callType,
        status: "pending",
        pricePerMinute: callData.pricePerMinute,
        creatorStreamName: callData.creatorStreamName,
        userStreamName: callData.userStreamName,
      });

      
      // INSERT_YOUR_CODE
      // Wait for 1 second before proceeding (e.g. to allow Firestore listeners to settle)
      await new Promise(resolve => setTimeout(resolve, 1000));
      callStore.subscribeToCall(callData.callId, callData.creatorId ?? creator.creatorId, () => {
        stopLocalMedia();
        onClose();
        toast({
          title: "通話已結束",
          description: "對方未接聽或已掛斷",
          status: "info",
          duration: 3000,
        });
      });
    } catch (error) {
      console.error("[CallModal] Failed to initiate call:", error);
      toast({
        title: "通話失敗",
        description: error.message || "無法發起通話",
        status: "error",
        duration: 3000,
      });
      onClose();
    } finally {
      setIsConnecting(false);
      callStore.setConnecting(false);
    }
  };

  /**
   * Fetch stream URLs when call becomes active
   */
  const fetchCallUrls = async () => {
    if (!callStore.currentCall?.callId) return;

    try {
      const response = await apiClient.getCallUrls(callStore.currentCall.callId);
      if (!response.success || !response.data) {
        throw new Error("Failed to get call URLs");
      }

      callStore.setCallUrls(response.data);

      // Start playing creator's stream
      if (playerRef.current && response.data.pullUrls?.webrtc) {
        playerRef.current.src(response.data.pullUrls.webrtc);
        playerRef.current.play();
        try {
          if (playerRef.current.on) {
            playerRef.current.on("error", (e) =>
              console.error("[CallModal] Player status: error", e)
            );
          }
        } catch (e) {
          console.warn("[CallModal] Could not attach player listeners", e);
        }
      }

      // Start duration timer (payment handled by server via request-auth)
      startDurationTimer();
    } catch (error) {
      console.error("[CallModal] Failed to fetch call URLs:", error);
    }
  };

  /**
   * Stop local media (pusher, timers, player, audio fallback). Shared by endCall and remote-end callback.
   */
  const stopLocalMedia = useCallback(() => {
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
    if (pollUrlsIntervalRef.current) {
      clearInterval(pollUrlsIntervalRef.current);
      pollUrlsIntervalRef.current = null;
    }
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    const pusher = pusherRef.current;
    if (pusher) {
      try {
        pusher.stopPush();
        // pusher.stopCamera();
        // pusher.stopMicrophone();
        if (pusher.destroy) pusher.destroy();
      } catch (e) {
        console.error("[CallModal] Pusher cleanup error:", e);
      }
      pusherRef.current = null;
    }
    if (playerRef.current) {
      playerRef.current.pause();
    }
    // Clean up audio fallback for voice calls
    if (audioFallbackRef.current) {
      try {
        audioFallbackRef.current.pause();
        audioFallbackRef.current.srcObject = null;
        audioFallbackRef.current.remove();
      } catch (e) {}
      audioFallbackRef.current = null;
    }
  }, []);

  /**
   * End the call
   * Close UI first so user sees modal dismiss immediately (e.g. when still "正在呼叫"),
   * then end call API in background.
   */
  const endCall = async () => {
    const callIdToEnd = callStore.currentCall?.callId;

    stopLocalMedia();

    // Close UI immediately so modal and "正在呼叫" disappear
    callStore.cleanup();
    onClose();

    // End call on backend in background (so creator/weaver state is cleared)
    if (callIdToEnd) {
      try {
        await apiClient.endCall(callIdToEnd);
      } catch (error) {
        console.error("[CallModal] Failed to end call:", error);
      }
    }
  };

  /**
   * Switch camera
   */
  const handleSwitchCamera = async () => {
    if (!pusherRef.current || cameraList.length < 2) return;
    
    const currentIndex = cameraList.findIndex(d => d.deviceId === currentCameraId);
    const nextIndex = (currentIndex + 1) % cameraList.length;
    const nextCameraId = cameraList[nextIndex].deviceId;
    
    try {
        const deviceManager = pusherRef.current.getDeviceManager();
        await deviceManager.switchCamera(nextCameraId);
        setCurrentCameraId(nextCameraId);
    } catch (error) {
        console.error("[CallModal] Switch camera failed:", error);
        toast({
            title: "切換鏡頭失敗",
            status: "error",
            duration: 2000
        });
    }
  };

  /**
   * Toggle mute (mute/unmute local mic in push stream).
   * Use pauseAudio/resumeAudio while pushing; start/stopMicrophone is not allowed when stream is pushing.
   */
  const toggleMute = async () => {
    const nextMuted = !isMuted;
    const pusher = pusherRef.current;
    if (!pusher) {
      setIsMuted(nextMuted);
      return;
    }
    try {
      if (nextMuted) {
        await pusher.pauseAudio();
      } else {
        await pusher.resumeAudio();
      }
      setIsMuted(nextMuted);
    } catch (e) {
      console.error("[CallModal] Toggle mute failed:", e);
      setIsMuted(!nextMuted); // revert UI on error
      toast({
        title: nextMuted ? "靜音失敗" : "取消靜音失敗",
        status: "error",
        duration: 2000
      });
    }
  };

  /**
   * Toggle video (camera on/off for push stream; video calls only).
   * Use pauseVideo/resumeVideo while pushing; start/stopCamera is not allowed when stream is pushing.
   */
  const toggleVideo = async () => {
    const nextVideoOff = !isVideoOff;
    const pusher = pusherRef.current;
    if (!pusher) {
      setIsVideoOff(nextVideoOff);
      return;
    }
    try {
      if (nextVideoOff) {
        await pusher.pauseVideo();
      } else {
        await pusher.resumeVideo();
      }
      setIsVideoOff(nextVideoOff);
    } catch (e) {
      console.error("[CallModal] Toggle video failed:", e);
      setIsVideoOff(!nextVideoOff);
      toast({
        title: nextVideoOff ? "關閉鏡頭失敗" : "開啟鏡頭失敗",
        status: "error",
        duration: 2000
      });
    }
  };

  /**
   * Try to create audio fallback for voice calls with audio-only streams.
   * Returns true if fallback was created.
   */
  const tryCreateAudioFallback = useCallback(() => {
    if (callType !== "voice" || audioFallbackRef.current) return false;
    
    const player = playerRef.current?.player;
    const videoEl = player?.el_?.querySelector?.('video') || player?.tech_?.el_;
    
    if (!videoEl?.srcObject || !(videoEl.srcObject instanceof MediaStream)) return false;
    
    // If video element has data, no fallback needed
    if (videoEl.readyState > 0) return false;
    
    const stream = videoEl.srcObject;
    const audioTracks = stream.getAudioTracks();
    
    if (audioTracks.length === 0) return false;
    
    // Create audio element fallback
    const audioEl = document.createElement('audio');
    audioEl.id = 'call-audio-fallback';
    audioEl.autoplay = true;
    audioEl.style.display = 'none';
    document.body.appendChild(audioEl);
    
    const audioOnlyStream = new MediaStream(audioTracks);
    audioEl.srcObject = audioOnlyStream;
    audioEl.play().catch(e => console.error("[CallModal] Audio fallback play failed:", e));
    
    audioFallbackRef.current = audioEl;
    setPlayerStatus("playing");
    return true;
  }, [callType]);

  /**
   * Handle player error with retry and audio fallback for voice calls
   */
  const handlePlayerError = useCallback((error) => {
    setPlayerStatus("error");
    console.error("[CallModal] player: error -> status error", error);
    
    // For voice calls, try audio fallback first (audio-only streams cause video errors)
    if (callType === "voice" && !audioFallbackRef.current) {
      if (tryCreateAudioFallback()) {
        return; // Audio fallback created, no need to retry video player
      }
    }
    
    // Check for -2002 error (Connect failed) or string variant
    const retryErrorCodes = [-2002, -2004];
    const isConnectError =
      retryErrorCodes.includes(error?.code) ||
      retryErrorCodes.includes(error?.data?.code) ||
      (typeof error === 'string' && retryErrorCodes.some(code => error.includes(String(code)))) ||
      (error?.message && retryErrorCodes.some(code => error.message.includes(String(code))));

    if (isConnectError) {
      const delay = 500;
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      retryTimerRef.current = setTimeout(() => {
        if (!modalOpenRef.current) return;
        const webrtcUrl = callStore.callUrls?.pullUrls?.webrtc;
        if (playerRef.current && webrtcUrl) {
          playerRef.current.src(webrtcUrl);
          playerRef.current.play();
          // For voice calls, also try audio fallback after retry
          if (callType === "voice") {
            setTimeout(() => tryCreateAudioFallback(), 500);
          }
        }
      }, delay);
    }
  }, [callType, tryCreateAudioFallback, callStore.callUrls?.pullUrls?.webrtc]);

  const handleLoadingChanged = useCallback((isLoading) => {
    setPlayerStatus((prev) =>
      isLoading ? "loading" : prev === "loading" ? "idle" : prev
    );
  }, []);

  /**
   * Handle media ready - try audio fallback for voice calls if video isn't playing
   */
  const handleMediaReady = useCallback(() => {
    // For voice calls, try audio fallback after media is ready but video might not play
    if (callType === "voice" && !audioFallbackRef.current) {
      // Delay slightly to see if video actually starts playing
      setTimeout(() => {
        if (!playerRef.current?.isPlaying) {
          tryCreateAudioFallback();
        }
      }, 300);
    }
  }, [callType, tryCreateAudioFallback]);

  /**
   * Handle playing state change to reset retry count
   */
  const handlePlayingChanged = useCallback((isPlaying) => {
    setPlayerStatus((prev) =>
      isPlaying ? "playing" : prev === "playing" ? "idle" : prev
    );
    if (isPlaying && retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const handlePlayChanged = useCallback(() => {
    if (callType === "voice" && !audioFallbackRef.current) {
      tryCreateAudioFallback();
    }
  }, []);

  /**
   * Format duration as MM:SS
   */
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Reset "already initiated" and player trace when modal closes
  useEffect(() => {
    if (!isOpen) {
      hasInitiatedRef.current = false;
      setPlayerStatus("idle");
    }
  }, [isOpen]);

  // Effect: Initiate call once when modal opens for this creator (ref + creatorId so we don't re-initiate on object identity change)
  useEffect(() => {
    const creatorId = creator?.creatorId;
    if (!isOpen || !creatorId) return;
    if (hasInitiatedRef.current) return;
    hasInitiatedRef.current = true;
    initiateCall();

    return () => {
      if (durationTimerRef.current) {
        clearInterval(durationTimerRef.current);
      }
      if (pollUrlsIntervalRef.current) {
        clearInterval(pollUrlsIntervalRef.current);
        pollUrlsIntervalRef.current = null;
      }
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      const pusher = pusherRef.current;
        if (pusher) {
          try {
            pusher.stopPush();
          // pusher.stopCamera();
          // pusher.stopMicrophone();
          if (pusher.destroy) pusher.destroy();
        } catch (e) {
          console.error("[CallModal] Pusher cleanup on unmount:", e);
        }
        pusherRef.current = null;
      }
    };
  }, [isOpen, creator?.creatorId]);

  // Poll for call URLs until creator accepts (no Firestore status; local state only)
  useEffect(() => {
    const isActive = callStore.currentCall?.status === "active";
    // Poll only if active and we don't have URLs yet
    if (!isOpen || !isActive || callStore.callUrls) {
      if (pollUrlsIntervalRef.current) {
        clearInterval(pollUrlsIntervalRef.current);
        pollUrlsIntervalRef.current = null;
      }
      return;
    }
    const poll = () => fetchCallUrls();
    poll(); // try once immediately
    pollUrlsIntervalRef.current = setInterval(poll, 2000);
    return () => {
      if (pollUrlsIntervalRef.current) {
        clearInterval(pollUrlsIntervalRef.current);
        pollUrlsIntervalRef.current = null;
      }
    };
  }, [isOpen, callStore.currentCall?.status, callStore.callUrls]);

  // Start user push when call URLs are available (so creator can see/hear the caller)
  // Delay slightly to ensure DOM element exists after conditional render
  useEffect(() => {
    const webrtcUrl = callStore.callUrls?.pushUrls?.webrtc;
    if (!isOpen || !webrtcUrl || pusherRef.current) return;
    pushRetryCountRef.current = 0;
    const effectiveCallType = callStore.currentCall?.callType ?? callType;
    // Wait for next frame to ensure call-local-preview element is in DOM
    const raf = requestAnimationFrame(() => {
      startUserPush(webrtcUrl, effectiveCallType);
    });
    return () => cancelAnimationFrame(raf);
  }, [
    isOpen,
    callType,
    callStore.callUrls?.pushUrls?.webrtc,
    callStore.currentCall?.callType,
    startUserPush,
  ]);

  // Mute local pusher preview audio to prevent echo (user hearing themselves)
  useEffect(() => {
    if (!isOpen || !callStore.callUrls?.pushUrls?.webrtc) return;
    const previewEl = document.getElementById("call-local-preview");
    if (!previewEl) return;

    const styleMediaInEl = (el) => {
      el.querySelectorAll("video").forEach((video) => {
        video.style.objectFit = "contain";
        video.style.width = "100%";
        video.style.height = "100%";
      });
      el.querySelectorAll("video, audio").forEach((media) => {
        if (!media.muted) media.muted = true;
      });
    };

    styleMediaInEl(previewEl);
    const observer = new MutationObserver(() => styleMediaInEl(previewEl));
    observer.observe(previewEl, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [isOpen, callStore.callUrls?.pushUrls?.webrtc]);

  // Set player src when callUrls is set and handle audio fallback for voice calls
  useEffect(() => {
    const pullUrl = callStore.callUrls?.pullUrls?.webrtc;
    if (!isOpen || !pullUrl) return;
    setPlayerStatus("loading");
    
    // Set source and play
    const srcTimeout = setTimeout(() => {
      const player = playerRef.current;
      if (player) {
        player.src(pullUrl);
        player.play();
      }
    }, 50);
    
    // For voice calls, keep retrying audio fallback every 500ms until successful
    // Creator may take indefinite time to start publishing
    let fallbackInterval;
    if (callType === "voice") {
      fallbackInterval = setInterval(() => {
        // Stop if already have fallback or video is playing
        if (audioFallbackRef.current || playerRef.current?.isPlaying) {
          clearInterval(fallbackInterval);
          return;
        }
        // Try to create audio fallback
        if (tryCreateAudioFallback()) {
          clearInterval(fallbackInterval);
        }
      }, 500);
    }
    
    return () => {
      clearTimeout(srcTimeout);
      if (fallbackInterval) clearInterval(fallbackInterval);
    };
  }, [isOpen, callType, callStore.callUrls?.pullUrls?.webrtc, tryCreateAudioFallback]);

  return (
    <Modal isOpen={isOpen} onClose={endCall} size="full" closeOnOverlayClick={false}>
      <ModalOverlay bg="black" />
      <ModalContent bg="black" m={0} h="100vh">
        <ModalBody 
            p={0} 
            position="relative" 
            h="100%" 
            display="flex" 
            justifyContent="center" 
            alignItems="center"
            overflow="hidden"
        >
          {/* Remote stream (StreamPlayer) - plays for both video and voice; layout aligned with streams page, resolution from source (contain) */}
          <Box
            position="absolute"
            top={0}
            left={0}
            width="100vw"
            height="100vh"
            overflow="hidden"
            bg="black"
            id="stream-player-container"
          >
            <StreamPlayer
              ref={playerRef}
              autoplay={true}
              fluid={false}
              responsive={false}
              muted={false}
              onError={handlePlayerError}
              onPlayingChanged={handlePlayingChanged}
              onPlayChanged={handlePlayChanged}
              onLoadingChanged={handleLoadingChanged}
              onMediaReady={handleMediaReady}
            />
            {!callStore.callUrls && (
              <Box
                position="absolute"
                inset={0}
                display="flex"
                alignItems="center"
                justifyContent="center"
                bg="black"
                zIndex={2}
              >
                <VStack>
                  <Spinner size="xl" color="white" thickness="4px" />
                  <Text color="white" mt={4}>建立連線中...</Text>
                  <Button
                    mt={6}
                    leftIcon={<FiPhoneOff />}
                    colorScheme="red"
                    variant="solid"
                    onClick={endCall}
                    borderRadius="full"
                  >
                    取消
                  </Button>
                </VStack>
              </Box>
            )}
          </Box>

          {/* Local preview: video = camera (TXLivePusher), voice = self avatar - Bottom Right */}
          {callStore.callUrls?.pushUrls?.webrtc && (
            <>
              {/* Pusher attach target: hidden for voice (mic only), visible for video */}
              {callType === "voice" ? (
                <Box
                  id="call-local-preview"
                  ref={localPreviewRef}
                  position="absolute"
                  w={1}
                  h={1}
                  opacity={0}
                  overflow="hidden"
                  pointerEvents="none"
                  aria-hidden="true"
                />
              ) : (
                <Box
                  id="call-local-preview"
                  ref={localPreviewRef}
                  position="absolute"
                  bottom="140px"
                  right="16px"
                  borderRadius="md"
                  border="2px solid white"
                  zIndex={10}
                  overflow="hidden"
                  boxShadow="xl"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bg="black"
                  sx={{
                    aspectRatio: isPortrait ? "9/16" : "16/9",
                    width: isPortrait
                      ? "min(40vw, 40vh * 0.5625)"
                      : "min(40vw, 40vh * 1.778)",
                    maxWidth: "45vw",
                    maxHeight: "45vh",
                  }}
                />
              )}
              {callType === "voice" && (
                <Box
                  position="absolute"
                  bottom="140px"
                  right="16px"
                  w="33.333vw"
                  h="33.333vh"
                  maxW="33.333vw"
                  maxH="33.333vh"
                  sx={{ aspectRatio: "9/16" }}
                  borderRadius="md"
                  border="2px solid white"
                  zIndex={10}
                  overflow="hidden"
                  boxShadow="xl"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  {/* Blurred creator picture filling background */}
                  <Box
                    position="absolute"
                    inset={0}
                    bg="blackAlpha.600"
                    bgImage={creator?.picture ? `url(${getPublicUrl(creator.picture)})` : undefined}
                    bgSize="cover"
                    bgPosition="center"
                    sx={{
                      filter: "blur(24px)",
                      transform: "scale(1.1)",
                    }}
                  />
                  <Box
                    position="absolute"
                    inset={0}
                    bg="blackAlpha.500"
                  />
                  <Box position="relative" zIndex={1}>
                    <Avatar
                      p={2}
                      size="2xl"
                      src={user?.picture ? getPublicUrl(user.picture) : user?.photoURL}
                      name={user?.displayName}
                    />
                  </Box>
                </Box>
              )}
            </>
          )}

          {/* Voice Call Background / Connecting State */}
          {(callType === "voice" || callStore.currentCall?.status !== "active") && (
            <Box
              position="absolute"
              inset={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              overflow="hidden"
            >
              {/* Blurred creator picture filling background */}
              <Box
                position="absolute"
                inset={0}
                bg="purple.900"
                bgImage={creator?.picture ? `url(${getPublicUrl(creator.picture)})` : undefined}
                bgSize="cover"
                bgPosition="center"
                sx={{
                  filter: "blur(24px)",
                  transform: "scale(1.1)",
                }}
              />
              <Box
                position="absolute"
                inset={0}
                bgGradient="linear(to-b, blackAlpha.600, blackAlpha.800)"
              />
              <VStack spacing={4} position="relative" zIndex={1}>
                <Avatar
                  size="2xl"
                  src={getPublicUrl(creator?.picture)}
                  name={creator?.displayName}
                />
                <Text fontSize="2xl" fontWeight="bold" color="white">
                  {creator?.displayName}
                </Text>
                {(isConnecting || (callStore.currentCall?.status === "pending" && !callStore.callUrls)) ? (
                  <HStack>
                    <Spinner size="sm" color="white" />
                    <Text color="whiteAlpha.700">正在呼叫...</Text>
                  </HStack>
                ) : (
                  <Text color="whiteAlpha.700">
                    {callType === "video" ? "視訊通話" : "語音通話"}
                  </Text>
                )}
              </VStack>
            </Box>
          )}

          {/* Call Info (Top) */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            p={4}
            bgGradient="linear(to-b, blackAlpha.700, transparent)"
          >
            <VStack align="start" spacing={1}>
              <HStack spacing={2} w="full" justify="space-between">
                <Text fontSize="lg" fontWeight="bold" color="white">
                  {creator?.displayName}
                </Text>
              </HStack>
              <HStack>
                <Text color="whiteAlpha.700">
                  {formatDuration(callDuration)}
                </Text>
                {callStore.isInCall && (
                  <HStack
                    bg="yellow.500"
                    px={2}
                    py={0.5}
                    borderRadius="full"
                    spacing={1}
                  >
                    <Text fontSize="sm" fontWeight="bold" color="black">
                      💎 {pricePerMinute}/分鐘
                    </Text>
                  </HStack>
                )}
              </HStack>
            </VStack>
          </Box>

          {/* Controls (Bottom) */}
          <Box
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            p={6}
            bgGradient="linear(to-t, blackAlpha.900, transparent)"
          >
            <HStack justify="center" spacing={8}>
              {/* Mute */}
              <VStack spacing={1}>
                <IconButton
                  aria-label="Mute"
                  icon={isMuted ? <FiMicOff /> : <FiMic />}
                  size="lg"
                  borderRadius="full"
                  bg={isMuted ? "whiteAlpha.300" : "whiteAlpha.200"}
                  color="white"
                  _hover={{ bg: "whiteAlpha.400" }}
                  onClick={async () => await toggleMute()}
                />
                <Text fontSize="xs" color="whiteAlpha.700">
                  靜音
                </Text>
              </VStack>

              {/* Video Toggle (only for video calls) */}
              {callType === "video" && (
                <>
                <VStack spacing={1}>
                  <IconButton
                    aria-label="Video"
                    icon={isVideoOff ? <FiVideoOff /> : <FiVideo />}
                    size="lg"
                    borderRadius="full"
                    bg={isVideoOff ? "whiteAlpha.300" : "whiteAlpha.200"}
                    color="white"
                    _hover={{ bg: "whiteAlpha.400" }}
                    onClick={async () => await toggleVideo()}
                  />
                  <Text fontSize="xs" color="whiteAlpha.700">
                    視訊
                  </Text>
                </VStack>

                {/* Switch Camera */}
                {cameraList.length > 1 && (
                    <VStack spacing={1}>
                        <IconButton
                            aria-label="Switch Camera"
                            icon={<FiRefreshCw />}
                            size="lg"
                            borderRadius="full"
                            bg="whiteAlpha.200"
                            color="white"
                            _hover={{ bg: "whiteAlpha.400" }}
                            onClick={handleSwitchCamera}
                        />
                        <Text fontSize="xs" color="whiteAlpha.700">
                            切換
                        </Text>
                    </VStack>
                )}
                </>
              )}

              {/* End Call */}
              <VStack spacing={1}>
                <IconButton
                  aria-label="End Call"
                  icon={<FiPhoneOff />}
                  size="lg"
                  borderRadius="full"
                  bg="red.500"
                  color="white"
                  _hover={{ bg: "red.600" }}
                  onClick={endCall}
                />
                <Text fontSize="xs" color="whiteAlpha.700">
                  掛斷
                </Text>
              </VStack>
            </HStack>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
});

export default CallModal;
